import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import {
  filterTravelersByCondition,
  getPassengerLabel,
  isEmptyObject,
  isStringEmpty,
  notEqual,
  parseEmptyArray,
} from '@common/helpers';
import {
  PassengerType,
  PREVIOUS_PAGE_IDENTIFIER,
  ServicingFunctionIdType,
  ServicingPageIdType,
} from '@common/interfaces';
import {
  ChangeLoungeData,
  PassengerApplicationStatusMap,
  SegmentMap,
  UpdateServiceInformation,
} from '@common/interfaces/mybooking/lounge-service-request.interface';
import {
  ChargeableLounge,
  ChargeableLoungePassengerApplyInfo,
  ChargeableLoungePassengerApplyStatusMap,
  Emds,
} from '@common/interfaces/mybooking/mybooking.interface';
import { Lounge, PassengerLoungeMap } from '@common/interfaces/mybooking/order-services.interface';
import { ServicingCommonStoreService } from '@common/services';
import { LoungeServiceRequestService } from '@common/services/my-booking/lounge-service-request/lounge-service-request.service';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SupportModalIdComponent } from '@lib/components/support-class';
import { DialogClickType, PageType, ReplaceParam } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService, DialogDisplayService, PageInitService } from '@lib/services';
import { of, Subscription } from 'rxjs';
import {
  Bound,
  GetOrderResponseData,
  GetOrderResponseDataOrderEligibilitiesChange,
  GetOrderResponseDataOrderType,
  GetOrderResponseDataTravelDocumentsInner,
  Traveler,
  Type1,
} from 'src/sdk-servicing';
import { LoungeServiceRequestHeaderComponent } from './lounge-service-request-header.component';
import { LoungeServiceRequestFooterComponent } from './lounge-service-request-footer.component';

@Component({
  selector: 'asw-lounge-service-request',
  templateUrl: './lounge-service-request.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoungeServiceRequestComponent extends SupportModalIdComponent implements AfterViewInit {
  public override autoInitEnd: boolean = false;
  functionId = ServicingFunctionIdType.BOOKING_CONFIRMATION;

  pageId = ServicingPageIdType.MYBOOKING_SERVICE;

  subFunctionId = ServicingFunctionIdType.SERVICE_REQUEST;

  subPageId = ServicingPageIdType.LOUNGE_SERVICE_REQUEST;

  pnr!: GetOrderResponseData;

  segmentInformationList: Type1[] = [];

  travelersByINF: Array<Traveler> = [];

  // 更新サービス情報
  updateServiceInformation: UpdateServiceInformation = {};

  screenInformationChangeFlag: boolean = false;

  subscription: Subscription = new Subscription();
  flights: Array<Type1> = [];
  travelers: Array<Traveler> = [];
  chargeableLounge!: ChargeableLounge;
  pageEndInitCalled: boolean = false;

  public override headerRef: LoungeServiceRequestHeaderComponent | null = null;
  public override footerRef: LoungeServiceRequestFooterComponent | null = null;

  // 当該セグメント情報
  constructor(
    private _common: CommonLibService,
    private _changeDetector: ChangeDetectorRef,
    private _dialogService: DialogDisplayService,
    private _pageInitService: PageInitService,
    private _myBookingService: MyBookingService,
    private _myBookingServiceService: MyBookingServiceService,
    private _loungeServiceRequestService: LoungeServiceRequestService,
    private _staticMsgPipe: StaticMsgPipe,
    private _servicingCommonStoreService: ServicingCommonStoreService
  ) {
    super(_common, _pageInitService);
  }

  init(): void {
    // (1.1) 画面の初期化処理(G01-003)の初期化開始処理を呼び出す。
    this._pageInitService.startInit();
    this.subscription.add(
      this._myBookingServiceService.myBookingServiceMasterData$.subscribe((master) => {
        this._loungeServiceRequestService.airportI18nAll = master.airportI18nAll ?? {};
      })
    );
    this.subscription.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.chargeableLounge = servicingCommon.chargeableLounge ?? ({} as ChargeableLounge);
        this.pnr = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this.travelers = this.pnr?.travelers ?? [];
        this.mappingInItLoungeServiceRequest();
        this._changeDetector.detectChanges();
      })
    );
    this.subscription.add(
      this._loungeServiceRequestService.screenInformationChangeFlag.subscribe((flag) => {
        this.screenInformationChangeFlag = flag;
        this._changeDetector.markForCheck();
      })
    );
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (this.headerRef) {
      this.headerRef.cancelEvent = () => {
        this.cancelEvent();
      };
    }
    if (this.footerRef) {
      this.footerRef.applyEvent = () => {
        this.applyEvent();
      };
    }
  }

  mappingInItLoungeServiceRequest() {
    this.flights =
      this.pnr?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[]);
    // [5] 搭乗者情報
    this.travelersByINF = filterTravelersByCondition(this.pnr, [
      {
        key: 'passengerTypeCode',
        value: PassengerType.INF,
        operator: notEqual,
      },
    ]);

    // (1.2) 画面情報に以下の内容を設定する。
    this.setInitialAswCommon();

    // (1.3) 画面にて変更した内容を保持するための「更新サービス情報」について以下の内容を設定し、保持する。
    // (1.3.1)
    this.updateServiceInformation = {
      airBoundId: '',
    };
    // (1.3.2)
    this.syncLoungeStore();

    // (1.4) 以下の通りに用意する。
    this.prepareProcessing();

    if (!this.pageEndInitCalled) {
      this._pageInitService.subDynamicInit(of({}));
      this._pageInitService.endInit(null);
      this.pageEndInitCalled = true;
    }
  }

  syncLoungeStore() {
    const chargeableLounge = this.chargeableLounge;
    for (let flights of this.flights ?? {}) {
      for (let traveler of this.travelersByINF) {
        const travelerItem = (chargeableLounge?.[flights?.id!] as ChargeableLoungePassengerApplyStatusMap)?.[
          traveler?.id!
        ] as ChargeableLoungePassengerApplyInfo;
        if (travelerItem) {
          const params: ChargeableLoungePassengerApplyInfo = {
            paidServiceAmount: travelerItem?.paidServiceAmount ?? 0,
            updateCategory: travelerItem?.updateCategory ?? '',
            ssrCode: travelerItem?.ssrCode ?? '',
          };
          this.addLoungeSelection(flights?.id ?? '', traveler?.id ?? '', params);
        }
      }
    }
  }

  /** (1.2) 画面情報に以下の内容を設定する。*/
  setInitialAswCommon() {
    this._common.aswCommonStoreService.updateAswCommon({
      functionId: this.functionId,
      pageId: this.pageId,
      subFunctionId: this.subFunctionId,
      subPageId: this.subPageId,
      isEnabledLogin: false,
      isUpgrade: false,
    });
  }

  prepareProcessing(): void {
    // (1.4.1) 変数を作成する。
    const segmentInformationList: Type1[] = [];
    const lounge = this.pnr.services?.lounge as Lounge;
    this.flights?.forEach((flight) => {
      // (1.4.2) サービシング共通store.PNR情報取得API応答.data.services.lounge.<当該セグメント情報.id>である情報を取得し、セグメントラウンジ情報とする。
      const segmentLoungeInformation = lounge?.[flight?.id!];
      const isPushFlight = this.travelersByINF.some(
        (traveler) => !isStringEmpty((segmentLoungeInformation?.[traveler?.id!] as PassengerLoungeMap)?.code)
      );
      // (1.4.3) セグメントラウンジ情報..isAvailable = true または 当該搭乗者情報.搭乗者ID.serviceStatus =  "confirm" または 当該搭乗者情報.搭乗者ID.serviceStatus = "confirmedWithoutTsm"かつ、PNR情報取得API応答.data.orderType.channelType="nh"の場合、
      if (segmentLoungeInformation?.isAvailable || isPushFlight) {
        segmentInformationList.push(flight);
      }
    });
    this.segmentInformationList = segmentInformationList;
  }

  // (2) 閉じるボタン押下処理
  cancelEvent = () => {
    // (2.1) 更新サービス情報.セグメントマップ≠サービシング共通store.ラウンジ情報.セグメントマップの場合、パラメータとして"MSG0524"「選択した内容が無効になる旨」の確認ダイアログ(G02-005)を表示する。
    if (this.screenInformationChangeFlag) {
      this.openConfirmDialog();
    } else {
      // (2.2) 上記以外の場合、当モーダルを閉じる。
      this.close();
    }
  };

  // (3) 申込ボタン押下処理
  applyEvent = async () => {
    let isStopProcess = false;
    // (3.1) チェックイン済みの航空券が存在するか判定する。
    isStopProcess = this.checkedInTicketExists();
    if (isStopProcess) return;
    // (3.2) ラウンジを選択した搭乗者にリレートしているEMDが存在するか判定を行う。
    isStopProcess = await this.EMDRelatedToThePassenger();
    if (isStopProcess) return;
    const chargeableLounge = this.createChargeableLoungeUpdate();
    const updateServicingParams = {
      // (3.3) 以下の内容をサービシング共通store.ラウンジ情報に設定する
      chargeableLounge: chargeableLounge,
      // (3.4) 以下の内容をサービシング共通storeで保持する。
      initDisplayService: 'lounge',
      isFirstDisplay: false,
    };
    this._servicingCommonStoreService.updateServicingCommon(updateServicingParams);
    // (3.5) 当モーダルを閉じ、予約詳細(S01-P030)の[初期表示処理]を呼び出す。
    this._myBookingService.runOnInit();
    this.close();
  };

  checkedInTicketExists(): boolean {
    return (
      this.pnr?.travelDocuments?.some((documentInformation) => {
        // (3.1.1) 以下条件すべてに該当する場合、処理を継続する。該当しない場合、次のドキュメント情報の繰り返し処理を行う。
        // (3.1.1.1)	当該ドキュメント情報.documentType="eticket"(航空券)
        // (3.1.1.2)	当該ドキュメント情報.fareInfosの中に、couponStatus="checkedIn"(チェックイン済み)であるレコードのindexが存在する
        const fareInfosIndex = parseEmptyArray(documentInformation?.fareInfos).findIndex(
          (item) =>
            item.couponStatus === GetOrderResponseDataOrderEligibilitiesChange.NonEligibilityReasonsEnum.CheckedIn
        );

        if (
          documentInformation?.documentType === GetOrderResponseDataTravelDocumentsInner.DocumentTypeEnum.Eticket &&
          fareInfosIndex >= 0
        ) {
          // (3.1.2)	当該ドキュメント情報.flightIds[index]であるflightIdを取得する。
          const flightID = documentInformation?.flightIds?.[fareInfosIndex];

          // (3.1.3)	以下の条件をすべて満たす場合、当画面でエラーメッセージを表示し、申込ボタン押下処理を終了する。
          // (3.1.3.1)	更新サービス情報.セグメントマップ.<flightId>が存在する
          const existFlightIDInSegmentMap = this.updateServiceInformation.segmentMap?.has(flightID!);
          if (existFlightIDInSegmentMap) {
            this._common.errorsHandlerService.setRetryableError(PageType.SUBPAGE, {
              errorMsgId: 'E0427',
              apiErrorCode: '',
              params: {} as ReplaceParam,
            });
            return true;
          }
        }
        return false;
      }) ?? false
    );
  }

  EMDRelatedToThePassenger = async (): Promise<boolean> => {
    for (let flight of this.flights) {
      // (3.2.1) 更新サービス情報.バウンドマップ.セグメントマップの中に、当該セグメント情報.idをキーとするオブジェクトが存在しない場合、次のセグメント情報の繰り返しを行う
      if (this.updateServiceInformation?.segmentMap?.has(String(flight?.id))) {
        const segmentMap = this.updateServiceInformation?.segmentMap?.get(String(flight?.id));
        // (3.2.2) 当該セグメント情報.idをキーとする更新サービス情報.バウンドマップ.セグメントマップを対象セグメントマップとして保持し、以下処理を行う。
        for (let traveler of this.travelers) {
          // (3.2.3) 以下の条件に合致した場合、PNR情報取得API応答.data.serviceSummary.emds.<当該搭乗者情報.id>を当該搭乗者リレート情報とする。それ以外の場合、次の搭乗者情報の繰り返しを行う。
          if (
            isEmptyObject((this.pnr?.serviceSummary?.emds as Emds)?.[String(traveler?.id)]) !== true &&
            segmentMap?.passengerApplicationStatusMap?.has(String(traveler?.id))
          ) {
            const travelerInformation = (this.pnr?.serviceSummary?.emds as Emds)?.[String(traveler?.id)];

            // (3.2.4) 以下条件をすべて満たす場合、パラメータとして"MSG0563"「ラウンジを選択した搭乗者にリレートしているかつ、セグメントにリレートしていない有料ラウンジのEMD情報が存在するため、過去に発行したEMDは後日払戻される旨」の確認ダイアログ(G02-005)を表示する。
            if (
              travelerInformation.chargeableLounge.hasEmd &&
              travelerInformation?.chargeableLounge?.hasSegmentAssociatedEmd !== true
            ) {
              const clickType = await this._loungeServiceRequestService.openDialogPromise('MSG0563');
              if (clickType === DialogClickType.CONFIRM) return false;
              else if (clickType === DialogClickType.CLOSE) {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  };

  createChargeableLoungeUpdate(): ChargeableLounge {
    let segmentMapData: ChargeableLoungePassengerApplyStatusMap = {};
    let totalAmount = 0;
    const segmentMap = this.updateServiceInformation?.segmentMap;
    for (const [flightId, flightInfo] of segmentMap ?? []) {
      for (const [travelerId, travelerInfo] of flightInfo?.passengerApplicationStatusMap ?? []) {
        totalAmount += travelerInfo?.paidServiceAmount ?? 0;
        segmentMapData = {
          ...segmentMapData,
          [flightId]: {
            ...segmentMapData?.[flightId],
            [travelerId]: travelerInfo,
          },
        };
      }
    }
    const chargeableLounge = {
      isApplicationProcessed: true,
      ...segmentMapData,
      totalAmount: totalAmount,
    };
    return chargeableLounge;
  }

  loungeSelectionValueChangeProcessing(changeLoungeData: ChangeLoungeData): void {
    // (4.2) 画面.ラウンジ選択をチェックOFFの場合、更新サービス情報を以下の通り更新する。
    if (changeLoungeData.isCheck !== true) {
      this.removeLoungeSelection(changeLoungeData.flightId, changeLoungeData.travelerId);
    }
    // (4.3) 画面.ラウンジ選択をチェックONの場合、更新サービス情報を以下の通り更新する。
    else {
      // (4.3.1) 画面.ラウンジ選択に紐づくSSRコード、有料サービス金額、搭乗者ID、セグメントIDを取得する。
      const passengerData: PassengerApplicationStatusMap = {
        ssrCode: changeLoungeData.ssrCode,
        paidServiceAmount: changeLoungeData.paidServiceAmount,
        updateCategory: 'request',
      };
      // (4.3.2) 搭乗者ID、セグメントIDを基に、セグメントマップと搭乗者別申込状況マップをチェックして、存在しない場合：追加する、存在した場合：更新する。
      this.addLoungeSelection(changeLoungeData.flightId, changeLoungeData.travelerId, passengerData);
    }
    // (4.4) 更新サービス情報.セグメントマップが""(空欄)の場合、更新サービス情報.画面情報変更フラグにfalse(未編集)を設定する。
    this._loungeServiceRequestService.screenInformationChangeFlag.next(this.isApplyActive());
  }

  addLoungeSelection(flightId: string, travelerId: string, passengerData: PassengerApplicationStatusMap) {
    let segmentMap = this.updateServiceInformation?.segmentMap;
    if (segmentMap?.size === undefined || segmentMap?.size === 0) {
      segmentMap = new Map<string, SegmentMap>();
    }
    if (segmentMap?.has(flightId) !== true) {
      segmentMap?.set(flightId, {
        passengerApplicationStatusMap: new Map<string, PassengerApplicationStatusMap>(),
      });
    }
    segmentMap?.get(flightId)?.passengerApplicationStatusMap?.set(travelerId, passengerData);
    this.updateServiceInformation = {
      airBoundId: this.pnr?.air?.bounds?.[0]?.airBoundId!,
      segmentMap: segmentMap,
    };
  }

  removeLoungeSelection(flightId: string, travelerId: string) {
    let segmentMap = this.updateServiceInformation?.segmentMap;
    if (segmentMap?.has(flightId)) {
      segmentMap?.get(flightId)!.passengerApplicationStatusMap.delete(travelerId);
      if (segmentMap?.get(flightId)!.passengerApplicationStatusMap.size === 0) {
        segmentMap?.delete(flightId);
      }
    }
    this.updateServiceInformation = {
      ...(segmentMap?.has(flightId) &&
        segmentMap?.size !== 0 && { airBoundId: this.pnr?.air?.bounds?.[0]?.airBoundId! }),
      segmentMap: segmentMap,
    };
  }

  openConfirmDialog(): void {
    this._dialogService
      .openDialog({ message: MasterJsonKeyPrefix.DYNAMIC + 'MSG0524' })
      .buttonClick$.subscribe((result) => {
        if (result.clickType === DialogClickType.CONFIRM) {
          this.close();
        }
      });
  }

  // [4] 出発地
  getAirportName(locationCode?: string, locationName?: string): string {
    let result = this._loungeServiceRequestService.onHandleCommonNo1(locationCode);
    if (result) {
      return result;
    }
    return locationName ?? '';
  }

  isLoungeExist(flightId?: string, travelerId?: string): boolean {
    const lounge = this.pnr.services?.lounge as Lounge;
    const segmentLoungeInformation = lounge?.[String(flightId)];
    return isEmptyObject((segmentLoungeInformation as any)?.[String(travelerId)]) !== true;
  }

  getParamsPassengerName(traveler: Traveler) {
    return {
      '0': traveler?.names?.[0]?.title,
      '1': traveler?.names?.[0]?.firstName,
      '2': traveler?.names?.[0]?.lastName,
    };
  }

  // [6] 搭乗者姓名
  getPassengerName(traveler: Traveler) {
    return this._staticMsgPipe.transform('label.passenger.n1', this.getParamsPassengerName(traveler));
  }

  // [7] 搭乗者種別
  getPassengerLabel(code?: string): string {
    return code ? this._staticMsgPipe.transform(getPassengerLabel(code)) : '';
  }

  /**
   * [8] 同伴搭乗者姓名
   * 当該搭乗者情報.accompanyingTravelerId ≠ “”(空欄)(同伴搭乗者がいる)場合、以下内容を併せて表示する。
   * @param traveler
   * @returns
   */
  isAccompanyingTraveler(traveler?: Traveler): boolean {
    return Boolean(traveler?.accompanyingTravelerId);
  }

  /**
   * [8] 同伴搭乗者姓名
   * サービシング共通store.PNR情報取得API応答.data.travelersを繰り返し、当該搭乗者情報.accompanyingTravelerId = 当該travelers.idとなるtravelers.name[0]について、title + ” ”(半角スペース) + firstName + ” ”(半角スペース) + lastName
   * @param traveler
   * @returns
   */
  getAccompanyingPassenger(traveler?: Traveler): string {
    const children = this.travelers.find((travel) => travel?.id === traveler?.accompanyingTravelerId) ?? {};
    return isEmptyObject(children) !== true
      ? this._staticMsgPipe.transform('label.passenger.n1', this.getParamsPassengerName(children))
      : '';
  }

  // [9] 同伴搭乗者種別
  getAccompanyingPassengerLabel(traveler?: Traveler): string {
    const children = this.travelers.find((travel) => travel?.id === traveler?.accompanyingTravelerId) ?? {};
    return children?.passengerTypeCode
      ? this._staticMsgPipe.transform(getPassengerLabel(children?.passengerTypeCode ?? ''))
      : '';
  }

  // [15] 申込ボタン
  isApplyActive(): boolean {
    return this.flights.some((flight) => {
      return this.travelersByINF.some((traveler) => {
        return this.isTravelerStatusDifferent(flight, traveler);
      });
    });
  }

  private isTravelerStatusDifferent(flight: Type1, traveler: Traveler): boolean {
    const travelerStoreItem = (this.chargeableLounge?.[flight?.id!] as ChargeableLoungePassengerApplyStatusMap)?.[
      traveler?.id!
    ] as ChargeableLoungePassengerApplyInfo;
    const travelerUpdateItem = this.updateServiceInformation?.segmentMap
      ?.get(flight?.id ?? '')
      ?.passengerApplicationStatusMap?.get(traveler?.id ?? '');
    return (
      JSON.stringify(travelerStoreItem ?? {}, Object.keys(travelerStoreItem ?? {}).sort()) !==
      JSON.stringify(travelerUpdateItem ?? {}, Object.keys(travelerUpdateItem ?? {}).sort())
    );
  }

  reload() {}

  destroy() {
    this._servicingCommonStoreService.updateServicingCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.LOUNGE_SERVICE_REQUEST,
    });
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
